import { createContext, useCallback, useContext, useRef, useState } from "react";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";

const Ctx = createContext(null);
let _confirm = null;

// Promise-based replacement for window.confirm; usable from anywhere via confirmDialog().
export const confirmDialog = (message, opts) => (_confirm ? _confirm(message, opts) : Promise.resolve(window.confirm(message)));

export function ConfirmProvider({ children }) {
  const [state, setState] = useState(null);
  const resolver = useRef(null);
  const ask = useCallback((message, opts = {}) => new Promise((resolve) => {
    resolver.current = resolve;
    setState({ message, title: opts.title || "Please confirm", okText: opts.okText || "Confirm", cancelText: opts.cancelText || "Cancel", destructive: opts.destructive ?? /delete|clear|remove|cannot be undone/i.test(message) });
  }), []);
  _confirm = ask;
  const done = (v) => { resolver.current?.(v); resolver.current = null; setState(null); };
  return (
    <Ctx.Provider value={ask}>
      {children}
      <AlertDialog open={!!state} onOpenChange={(o) => { if (!o) done(false); }}>
        <AlertDialogContent data-testid="confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>{state?.title}</AlertDialogTitle>
            <AlertDialogDescription className="whitespace-pre-line">{state?.message}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => done(false)} data-testid="confirm-cancel">{state?.cancelText}</AlertDialogCancel>
            <AlertDialogAction onClick={() => done(true)} className={state?.destructive ? "bg-destructive text-destructive-foreground hover:bg-destructive/90" : ""} data-testid="confirm-ok">{state?.okText}</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Ctx.Provider>
  );
}

export const useConfirm = () => useContext(Ctx) || confirmDialog;
